import { useEffect, useState } from 'react';
import {
  Box, Stack, Typography, ToggleButton, ToggleButtonGroup, alpha, useTheme,
  Radio, CircularProgress, Chip, Avatar,
} from '@mui/material';
import { AutoMode, TouchApp, Star, DirectionsCar } from '@mui/icons-material';
import { searchDispatchPartners } from '../../services/dispatcherService';
import type { DispatchMode, PartnerCandidate } from './dispatchBookUtils';
import { bookSegSx, bookChoiceSx } from './dispatchTheme';

interface DispatchPartnerAssignStepProps {
  partnerType: 'driver' | 'delivery_partner' | 'service_provider';
  dispatchMode: DispatchMode;
  onDispatchModeChange: (mode: DispatchMode) => void;
  selectedPartnerId: string;
  onPartnerSelect: (partnerId: string) => void;
  coordinates?: { lat: number; lng: number } | null;
  vehicleTypeFilter?: string;
  enabled?: boolean;
  compact?: boolean;
}

export default function DispatchPartnerAssignStep({
  partnerType,
  dispatchMode,
  onDispatchModeChange,
  selectedPartnerId,
  onPartnerSelect,
  coordinates,
  vehicleTypeFilter,
  enabled = true,
  compact = false,
}: DispatchPartnerAssignStepProps) {
  const theme = useTheme();
  const [partners, setPartners] = useState<PartnerCandidate[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || dispatchMode !== 'MANUAL') {
      setPartners([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    searchDispatchPartners({
      type: partnerType,
      lat: coordinates?.lat,
      lng: coordinates?.lng,
    })
      .then((rows) => {
        if (cancelled) return;
        let list = (rows || []) as PartnerCandidate[];
        if (vehicleTypeFilter) {
          list = list.filter((p) => !p.vehicleType || p.vehicleType.toLowerCase() === vehicleTypeFilter.toLowerCase());
        }
        setPartners(list);
        if (list.length && !selectedPartnerId) {
          onPartnerSelect(list[0].userId);
        }
      })
      .catch(() => setPartners([]))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [enabled, dispatchMode, coordinates?.lat, coordinates?.lng, partnerType, vehicleTypeFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const partnerLabel = partnerType === 'driver' ? 'Driver' : partnerType === 'delivery_partner' ? 'Delivery Partner' : 'Service Provider';

  return (
    <Stack spacing={compact ? 1 : 2}>
      <Typography variant="caption" fontWeight={800} color="text.secondary" letterSpacing="0.08em">
        DISPATCH
      </Typography>
      <ToggleButtonGroup
        exclusive
        fullWidth
        value={dispatchMode}
        onChange={(_, v) => v && onDispatchModeChange(v)}
        sx={bookSegSx(theme.palette.primary.main)}
      >
        <ToggleButton value="AUTOMATIC">
          <Stack direction={compact ? 'row' : 'column'} alignItems="center" spacing={compact ? 0.75 : 0.5}>
            <AutoMode fontSize="small" />
            <Typography variant="caption" fontWeight={700}>Automatic</Typography>
            {!compact && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Offer cascade
              </Typography>
            )}
          </Stack>
        </ToggleButton>
        <ToggleButton value="MANUAL">
          <Stack direction={compact ? 'row' : 'column'} alignItems="center" spacing={compact ? 0.75 : 0.5}>
            <TouchApp fontSize="small" />
            <Typography variant="caption" fontWeight={700}>Manual</Typography>
            {!compact && (
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                Pick nearest
              </Typography>
            )}
          </Stack>
        </ToggleButton>
      </ToggleButtonGroup>

      {dispatchMode === 'AUTOMATIC' && !compact && (
        <Box
          sx={{
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.primary.main, 0.06),
            border: 1,
            borderColor: alpha(theme.palette.primary.main, 0.15),
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Nearest {partnerLabel.toLowerCase()} receives an offer. If rejected or timed out, the next nearest partner is offered automatically — like Uber / Red Taxi.
          </Typography>
        </Box>
      )}

      {dispatchMode === 'MANUAL' && (
        <Stack spacing={1} sx={compact ? { maxHeight: 160, overflow: 'auto' } : undefined}>
          {!compact && (
            <Typography variant="body2" color="text.secondary">
              Select the nearest available {partnerLabel.toLowerCase()} to assign immediately.
            </Typography>
          )}
          {loading && (
            <Stack alignItems="center" py={2}>
              <CircularProgress size={28} />
            </Stack>
          )}
          {!loading && !partners.length && (
            <Typography variant="body2" color="warning.main">
              No online {partnerLabel.toLowerCase()}s nearby. Job will enter the queue unassigned.
            </Typography>
          )}
          {!loading && partners.map((p) => (
            <Box
              key={p.userId}
              onClick={() => onPartnerSelect(p.userId)}
              sx={{
                p: 1.15,
                ...bookChoiceSx(selectedPartnerId === p.userId, theme.palette.primary.main),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Radio checked={selectedPartnerId === p.userId} size="small" />
                <Avatar sx={{ width: 36, height: 36, bgcolor: alpha(theme.palette.primary.main, 0.15), color: 'primary.main' }}>
                  <DirectionsCar fontSize="small" />
                </Avatar>
                <Box flex={1}>
                  <Typography variant="body2" fontWeight={700}>{p.name}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.phone || p.email || '—'}</Typography>
                </Box>
                <Stack alignItems="flex-end" spacing={0.25}>
                  {p.distanceKm != null && (
                    <Chip label={`${p.distanceKm.toFixed(1)} km`} size="small" variant="outlined" />
                  )}
                  {p.rating != null && (
                    <Stack direction="row" alignItems="center" spacing={0.25}>
                      <Star sx={{ fontSize: 14, color: 'warning.main' }} />
                      <Typography variant="caption">{p.rating.toFixed(1)}</Typography>
                    </Stack>
                  )}
                  {p.vehicleType && (
                    <Typography variant="caption" color="text.secondary">{p.vehicleType}</Typography>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
}
